/**
 * The decide phase as a function (ARCHITECTURE §5.16, *The decide phase on workers*, step 1): every living unit decides
 * from one start-of-tick world — its brain if it is a bot nobody holds, the held unit's eyes (`observe`) if a person holds
 * it — and what a brain would have staged on the Match (its memory, its team's tracks, its inputs) comes back as the
 * unit's return, in the order it was staged. The Match replays the returns in the players array's order, so the staged
 * memory and inputs are what they were when the brains staged them on the Match directly.
 *
 * A brain sees only a `DecideView`: the state, the tick, the last tick's shots, and the three staging calls. The Match is
 * one (structurally), so a brain handed the Match itself still runs; the decide phase hands each brain a per-unit view.
 * Pure: nothing here touches the Match.
 */
import type { ActionBrainState, GameState, MatchInputBody, PlayerState, UnitIntent, Vec3 } from '../types';

/** What a brain may read of the world and do with it while it decides. */
export interface DecideView {
  readonly state: GameState;
  readonly actionTick: number;
  /** Who fired in the last tick (the hearing), keyed by unit. */
  readonly lastShot: Readonly<Record<number, { pos: Vec3; tick: number; loud?: number; step?: boolean }>>;
  /** The unit's memory after deciding (64.8): committed once every unit has decided. */
  stageBrain(unitId: number, brain: ActionBrainState): void;
  /** An input due this tick (R11 step 6): it enters through `apply` after every unit has decided, in staging order. */
  stageInput(unitId: number, body: MatchInputBody): void;
  /**
   * §5.16 step 3: a unit tells its team's tracks (L1) — a sighting (`entry`), or a witnessed death (null). The Match applies
   * every unit's edits in the players' order onto the start-of-tick tracks once every unit has decided.
   */
  stageTrackEdit(team: number, unitId: number, entry: { pos: Vec3; tick: number } | null): void;
}

/** A brain: the unit's intent from the view; and, optionally, a held unit's eyes (68.26). */
export type DecideDriver = ((view: DecideView, unit: PlayerState) => UnitIntent) & { observe?: (view: DecideView, unit: PlayerState) => void };

/** One staging call, as the brain made it. */
export type Staged =
  | { kind: 'brain'; unitId: number; brain: ActionBrainState }
  | { kind: 'input'; unitId: number; body: MatchInputBody }
  | { kind: 'track'; team: number; id: number; entry: { pos: Vec3; tick: number } | null };

/** A unit's decision: `ran` when its brain decided (its intent the brain's), else the Match's own; and what it staged. */
export interface Decided { ran: boolean; intent: UnitIntent | undefined; staged: Staged[] }

/**
 * Every unit's decision, in the players array's order. `decides(unit)`: a bot nobody holds (its brain runs); `observes(unit)`:
 * a unit a person holds (its eyes look, when the driver has them). Units that do neither decide nothing.
 */
export function decidePhase(world: Pick<DecideView, 'state' | 'actionTick' | 'lastShot'>, driver: DecideDriver | null,
  decides: (unit: PlayerState) => boolean, observes: (unit: PlayerState) => boolean): Decided[] {
  return world.state.players.map(unit => {
    const staged: Staged[] = [];
    if (!driver) return { ran: false, intent: undefined, staged };
    const view: DecideView = {
      state: world.state, actionTick: world.actionTick, lastShot: world.lastShot,
      stageBrain: (unitId, brain) => { staged.push({ kind: 'brain', unitId, brain }); },
      stageInput: (unitId, body) => { staged.push({ kind: 'input', unitId, body }); },
      stageTrackEdit: (team, id, entry) => { staged.push({ kind: 'track', team, id, entry }); },
    };
    if (decides(unit)) return { ran: true, intent: driver(view, unit), staged };
    if (observes(unit)) driver.observe?.(view, unit);
    return { ran: false, intent: undefined, staged };
  });
}
